/**
 * Past-week review — Eva picks an archived week and runs the full
 * gameplay loop against it. Spec §5.4.
 *
 * Mechanically identical to a Play Today session — same flash card,
 * first pass, results, second chance, 3x-write, celebration. The
 * difference is on the wire: `countsTowardProgress = false`. The
 * achievement/streak/mastery engines all short-circuit on that flag,
 * so past-week reviews persist as session rows for the parent
 * dashboard but never bump the streak or unlock new stickers.
 *
 * Loading model:
 *   - On mount: fetch the weeks list + parent settings in one tick.
 *   - Render the archived weeks newest-first.
 *   - On tap: stash the chosen week + settings into SessionContext
 *     with `countsTowardProgress: false`, then navigate to
 *     `flashCard`. FlashCardScreen sees a pre-populated session and
 *     skips its own fetch, jumping straight to "ready".
 */
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/Button";
import { PumpkinFigure } from "@/components/PumpkinFigure";
import { useNavigation } from "@/context/NavigationContext";
import { useSession } from "@/context/SessionContext";
import { api } from "@/lib/api";
import type { Settings, Week } from "@/types/data";
import "./PastWeeksScreen.css";

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; archived: Week[]; settings: Settings };

export function PastWeeksScreen() {
  const { navigate } = useNavigation();
  const { startSession } = useSession();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [weeksRes, settingsRes] = await Promise.all([
        api.listWeeks(),
        api.getSettings(),
      ]);
      if (cancelled) return;
      if (weeksRes.status !== 200 || !weeksRes.body) {
        setState({ kind: "error", message: "Couldn't load past weeks." });
        return;
      }
      if (settingsRes.status !== 200 || !settingsRes.body) {
        setState({ kind: "error", message: "Couldn't load settings." });
        return;
      }
      setState({
        kind: "ready",
        archived: weeksRes.body.archived,
        settings: settingsRes.body,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleStart = useCallback(
    (week: Week, settings: Settings) => {
      startSession({
        weekId: week.id,
        words: week.words,
        settings,
        countsTowardProgress: false,
      });
      navigate("flashCard");
    },
    [startSession, navigate],
  );

  if (state.kind === "loading") {
    return (
      <main className="past-weeks">
        <p className="past-weeks__loading">Getting your past weeks ready…</p>
      </main>
    );
  }

  if (state.kind === "error") {
    return (
      <main className="past-weeks">
        <p className="past-weeks__error">{state.message}</p>
        <Button onClick={() => navigate("home")}>Back to home</Button>
      </main>
    );
  }

  if (state.archived.length === 0) {
    return (
      <main className="past-weeks">
        <header className="past-weeks__header">
          <h1 className="past-weeks__title">Past words</h1>
        </header>
        <div className="past-weeks__empty">
          <PumpkinFigure
            className="past-weeks__pumpkin"
            pose="curledRest"
            alt="Pumpkin the pangolin, resting"
          />
          <p className="past-weeks__empty-line">
            No past weeks yet — you&rsquo;ll have some here once you&rsquo;ve
            done a few weeks of practice.
          </p>
          <Button onClick={() => navigate("home")}>Back to home</Button>
        </div>
      </main>
    );
  }

  return (
    <main className="past-weeks">
      <header className="past-weeks__header">
        <h1 className="past-weeks__title">Past words</h1>
        <Button size="small" variant="secondary" onClick={() => navigate("home")}>
          Back
        </Button>
      </header>

      <ul className="past-weeks__list">
        {state.archived.map((w) => (
          <li key={w.id} className="past-weeks__row">
            <button
              type="button"
              className="past-weeks__tap"
              onClick={() => handleStart(w, state.settings)}
              aria-label={`Practise ${w.label ?? `Week ${w.id}`}`}
            >
              <div className="past-weeks__row-main">
                <span className="past-weeks__row-title">
                  {w.label ?? `Week ${w.id}`}
                </span>
                <span className="past-weeks__row-count">
                  {w.words.length} {w.words.length === 1 ? "word" : "words"}
                </span>
              </div>
              <span className="past-weeks__row-preview">
                {/* Show the first few spellings as a peek so Matt can spot
                   the right week without expanding it. */}
                {w.words.slice(0, 4).map((wd) => wd.spelling).join(", ")}
                {w.words.length > 4 ? "…" : ""}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <p className="past-weeks__hint">
        Playing past words is just for practice — it won&rsquo;t change
        your streak.
      </p>
    </main>
  );
}
